import React from 'react';
import { Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../../../api/shop/types/productTypes';
import { StatusBadge } from '../../../../Common/BadgeStyles';
import '../../../../Common/BadgeStyles.css';
import { getImageUrl } from '../../../../utils/imageUrl';
import './ProductCard.css';

interface ProductCardProps {
  product: Product;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleStatus?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/shop-services/products/${product._id}`);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    } else {
      navigate(`/shop-services/products/${product._id}/edit`);
    }
  };
  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getProductImage = () => {
    // First try direct image (already processed by service)
    if (product.image) {
      return getImageUrl(product.image);
    }
    
    // Then try colorImages
    if (product.colorImages && typeof product.colorImages === 'object') {
      const colorKeys = Object.keys(product.colorImages);
      if (colorKeys.length > 0 && Array.isArray(product.colorImages[colorKeys[0]])) {
        const firstColorImages = product.colorImages[colorKeys[0]];
        if (firstColorImages.length > 0) {
          return getImageUrl(firstColorImages[0]);
        }
      }
    }
    
    // Fallback to default icon
    return getImageUrl('admin/static-assets/icons/shop-service/product.svg');
  };

  const displayPrice = product.price || 0;
  const displayMrp = product.mrp || product.price || 0;
  const discount = product.discount || (displayMrp > displayPrice ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100) : 0);
  
  // Format category and sport
  const categoryText = product.category && product.sport 
    ? `${product.sport} • ${product.category}`
    : product.category || product.sport || '';

  return (
    <Card className="product-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <Card.Body className="product-card-body">
        {/* Product Image - Left Side */}
        <div className="product-card-image-container">
          <img
            src={getProductImage()}
            alt={product.name}
            className="product-card-image"
            onError={(e) => {
              (e.target as HTMLImageElement).src = getImageUrl('admin/static-assets/icons/shop-service/product.svg');
            }}
          />
        </div>

        {/* Product Details - Right Side */}
        <div className="product-card-details">
          {/* Main Content */}
          <div className="product-card-content">
            {/* Product Title - Row 1 */}
            <div className="product-card-header">
              <h5 className="product-card-title" title={product.name}>
                {product.name}
              </h5>
            </div>

            {/* Category and Sport - Row 2 */}
            <div className="product-card-category-section">
              <p className="product-card-category">
                {categoryText || 'N/A'}
              </p>
            </div>

            {/* Pricing - Row 3 */}
            <div className="product-card-pricing-section">
              <div className="product-card-prices">
                <span className="product-card-selling-price">{formatPrice(displayPrice)}</span>
                {displayMrp > displayPrice && (
                  <span className="product-card-mrp">{formatPrice(displayMrp)}</span>
                )}
              </div>
              {discount > 0 && (
                <span className="product-card-offer-badge">{discount}% OFF</span>
              )}
            </div>

            {/* Status Badge - Row 4 */}
            <div className="product-card-status-section">
              <StatusBadge status={product.status as 'ACTIVE' | 'INACTIVE'} />
            </div>
          </div>

          {/* Bottom Actions: Toggle and Buttons (only when callbacks provided) */}
          {(onToggleStatus || onEdit || onDelete) && (
            <div className="d-flex justify-content-between align-items-center mt-auto product-card-actions">
              {onToggleStatus && (
                <div className="form-check form-switch">
                  <input
                    className={`form-check-input product-card-toggle ${
                      product.status === 'ACTIVE' ? 'active' : 'inactive'
                    }`}
                    type="checkbox"
                    role="switch"
                    checked={product.status === 'ACTIVE'}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleStatus();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              <div className="d-flex gap-2 product-card-buttons-container">
                {onEdit && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="product-action-btn product-edit-btn"
                    onClick={handleEditClick}
                    title="Edit Product"
                  >
                    Edit
                    <img
                      src={getImageUrl('admin/static-assets/icons/general/edit-icon.svg')}
                      alt="Edit"
                      className="product-icon"
                      style={{ width: '10px', height: '10px' }}
                    />
                  </Button>
                )}
                {onDelete && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="product-action-btn product-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  >
                    Delete
                    <img
                      src={getImageUrl('admin/static-assets/icons/general/delete-icon.svg')}
                      alt="Delete"
                      className="product-icon"
                      style={{ width: '10px', height: '10px' }}
                    />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ProductCard;
